// Purpose: Specialized error boundary for evidence components
// Audience: Evidence display components that need graceful error handling
// Features:
// - Evidence-specific error messages
// - Fallback evidence display
// - Error reporting for evidence issues

import React from 'react';
import PropTypes from 'prop-types';
import ErrorBoundary from '../common/ErrorBoundary';
import { FaFileAlt, FaExclamationTriangle } from 'react-icons/fa';
import '../../styles/ErrorBoundary.css';

const EvidenceErrorBoundary = ({ 
  children, 
  evidenceFileName, 
  evidenceId,
  mode = 'tile',
  onError 
}) => {
  const handleError = (error, errorInfo) => {
    // Log evidence-specific error context
    console.error('Evidence component error:', {
      evidenceFileName,
      evidenceId,
      mode,
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Call parent error handler if provided
    if (onError) {
      onError(error, errorInfo, { evidenceFileName, evidenceId, mode });
    }
  };

  const evidenceFallback = (error, retry) => {
    const isCompact = mode === 'compact';
    
    return (
      <div className={`evidence-error-fallback ${mode}`}>
        <div className="evidence-error-content">
          <div className="evidence-error-icon">
            <FaExclamationTriangle />
          </div>
          <div className="evidence-error-info">
            <h4 className="evidence-error-title">
              {isCompact ? 'Error' : 'Unable to display evidence'}
            </h4>
            {evidenceFileName && (
              <p className="evidence-error-filename">
                <FaFileAlt aria-hidden="true" />
                {evidenceFileName}
              </p>
            )}
            {!isCompact && (
              <p className="evidence-error-message">
                This evidence file could not be loaded. This might be due to a 
                network issue or the file being corrupted.
              </p>
            )}
            <button 
              className="evidence-error-retry"
              onClick={retry}
              type="button"
              aria-label={`Retry loading ${evidenceFileName || 'evidence'}`}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary
      fallback={evidenceFallback}
      onError={handleError}
      showRetry={false} // We handle retry in our custom fallback
    >
      {children}
    </ErrorBoundary>
  );
};

EvidenceErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  evidenceFileName: PropTypes.string,
  evidenceId: PropTypes.string,
  mode: PropTypes.oneOf(['tile', 'modal', 'compact']),
  onError: PropTypes.func
};

export default EvidenceErrorBoundary;
